import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Badge,
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Popper,
  Typography,
  useMediaQuery
} from '@mui/material';

// project import
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import { useGiraf } from 'giraf/index';

// assets
import { BellOutlined, CheckOutlined, CloseOutlined, WarningOutlined, InfoCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';

const BASE = process.env.REACT_APP_API_URL;

const avatarSX = { width: 36, height: 36, fontSize: '1rem' };
const actionSX = { mt: '6px', ml: 1, top: 'auto', right: 'auto', alignSelf: 'flex-start', transform: 'none' };

const TYPE_META = {
  alert: { color: 'error.main', bg: 'error.lighter', icon: <WarningOutlined /> },
  warning: { color: 'warning.main', bg: 'warning.lighter', icon: <WarningOutlined /> },
  success: { color: 'success.main', bg: 'success.lighter', icon: <CheckCircleOutlined /> },
  info: { color: 'primary.main', bg: 'primary.lighter', icon: <InfoCircleOutlined /> }
};

const timeAgo = (d) => {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const Notification = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { gHead } = useGiraf();
  const token = gHead?.AuthToken;

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

  const fetchItems = useCallback(() => {
    if (!token) return;
    axios
      .get(`${BASE}/notifications`, { headers: { Authorization: token } })
      .then(({ data }) => setItems(data.data || []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    fetchItems();
    const id = setInterval(fetchItems, 60000); // refresh badge every 60s
    return () => clearInterval(id);
  }, [fetchItems]);

  const unread = items.filter((n) => !n.is_read).length;

  const handleToggle = () => {
    setOpen((prev) => {
      if (!prev) fetchItems();
      return !prev;
    });
  };
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const markRead = (id) => {
    axios.patch(`${BASE}/notifications/${id}/read`, {}, { headers: { Authorization: token } }).catch(() => {});
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
  };

  const markAllRead = () => {
    axios.patch(`${BASE}/notifications/read-all`, {}, { headers: { Authorization: token } }).catch(() => {});
    setItems((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
  };

  const openItem = (n) => {
    markRead(n.id);
    setOpen(false);
    navigate('/notifications');
  };

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const iconBackColorOpen = 'grey.300';
  const iconBackColor = 'grey.100';

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        disableRipple
        color="secondary"
        sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : iconBackColor }}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unread} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [matchesXs ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down('md')]: { maxWidth: 285 }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notifications"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <Box>
                      {unread > 0 && (
                        <IconButton size="small" title="Mark all read" onClick={markAllRead}>
                          <CheckOutlined />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={handleToggle}>
                        <CloseOutlined />
                      </IconButton>
                    </Box>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      maxHeight: 380,
                      overflowY: 'auto',
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {items.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        <BellOutlined style={{ fontSize: 24 }} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          No notifications yet.
                        </Typography>
                      </Box>
                    ) : (
                      items.slice(0, 6).map((n, i) => {
                        const meta = TYPE_META[n.type] || TYPE_META.info;
                        return (
                          <Box key={n.id}>
                            <ListItemButton onClick={() => openItem(n)} sx={{ bgcolor: n.is_read ? 'transparent' : 'action.hover' }}>
                              <ListItemAvatar>
                                <Avatar sx={{ color: meta.color, bgcolor: meta.bg }}>{meta.icon}</Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle1" sx={{ fontWeight: n.is_read ? 400 : 700 }}>
                                    {n.title}
                                  </Typography>
                                }
                                secondary={
                                  n.body ? (
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {n.body}
                                    </Typography>
                                  ) : null
                                }
                              />
                              <ListItemSecondaryAction>
                                <Typography variant="caption" noWrap>
                                  {timeAgo(n.created_at)}
                                </Typography>
                              </ListItemSecondaryAction>
                            </ListItemButton>
                            {i < Math.min(items.length, 6) - 1 && <Divider />}
                          </Box>
                        );
                      })
                    )}
                    <Divider />
                    <ListItemButton
                      sx={{ textAlign: 'center', py: `${12}px !important` }}
                      onClick={() => {
                        setOpen(false);
                        navigate('/notifications');
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            View All
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Notification;
